import { Suspense } from 'react';
import { routeLazy } from '@/lib/routeLazy';
import { Route, Switch } from 'wouter';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ThemeProvider } from '@/contexts/ThemeContext';
import Home from '@/pages/Home';
import NotFound from '@/pages/NotFound';
// Practice areas
const ProbatePage = routeLazy(() => import('@/pages/ProbatePage'));
const EstatePlanningPage = routeLazy(() => import('@/pages/EstatePlanningPage'));
const TrustsPage = routeLazy(() => import('@/pages/TrustsPage'));
const AssetProtectionPage = routeLazy(() => import('@/pages/AssetProtectionPage'));
const GuardianshipPage = routeLazy(() => import('@/pages/GuardianshipPage'));
const PowerOfAttorneyPage = routeLazy(() => import('@/pages/PowerOfAttorneyPage'));
const ElderLawPage = routeLazy(() => import('@/pages/ElderLawPage'));
const FamilyLawPage = routeLazy(() => import('@/pages/FamilyLawPage'));
const MatrimonialPage = routeLazy(() => import('@/pages/MatrimonialPage'));
const RealEstatePage = routeLazy(() => import('@/pages/RealEstatePage'));
const PersonalInjuryPage = routeLazy(() => import('@/pages/PersonalInjuryPage'));
const MedicaidPlanningPage = routeLazy(() => import('@/pages/MedicaidPlanningPage'));
const WillsPage = routeLazy(() => import('@/pages/WillsPage'));
const WillsAndTrustsPage = routeLazy(() => import('@/pages/WillsAndTrustsPage'));
const CivilRightsPage = routeLazy(() => import('@/pages/CivilRightsPage'));
// Attorney bios
const RusselMorganEsqPage = routeLazy(() => import('@/pages/attorneys/RusselMorganEsqPage'));
const RusselMorganEPLawyerPage = routeLazy(() => import('@/pages/attorneys/RusselMorganEPLawyerPage'));
const SimgeKarabogaPage = routeLazy(() => import('@/pages/attorneys/SimgeKarabogaPage'));
const GuyChiricoPage = routeLazy(() => import('@/pages/attorneys/GuyChiricoPage'));
const AlanVaitzmanPage = routeLazy(() => import('@/pages/attorneys/AlanVaitzmanPage'));
const PeterGathingsPage = routeLazy(() => import('@/pages/attorneys/PeterGathingsPage'));
const SofiaCristandoPage = routeLazy(() => import('@/pages/attorneys/SofiaCristandoPage'));
const AttorneysIndexPage = routeLazy(() => import('@/pages/AttorneysIndexPage'));
const BiancaOlliverPage = routeLazy(() => import('@/pages/attorneys/BiancaOlliverPage'));
const JamesThreattPage = routeLazy(() => import('@/pages/attorneys/JamesThreattPage'));
const JacquelineBohatchPage = routeLazy(() => import('@/pages/attorneys/JacquelineBohatchPage'));
const SabrinaVanCampPage = routeLazy(() => import('@/pages/attorneys/SabrinaVanCampPage'));
const AllanLouisPage = routeLazy(() => import('@/pages/attorneys/AllanLouisPage'));
const ShanieceHunterPage = routeLazy(() => import('@/pages/attorneys/ShanieceHunterPage'));
const LizaGrossPage = routeLazy(() => import('@/pages/attorneys/LizaGrossPage'));
const GabriellaVaitzmanPage = routeLazy(() => import('@/pages/attorneys/GabriellaVaitzmanPage'));
const CharlesMarchesePage = routeLazy(() => import('@/pages/attorneys/CharlesMarchesePage'));
// Marketing
const AboutUsPage = routeLazy(() => import('@/pages/marketing/AboutUsPage'));
const OurFeesPage = routeLazy(() => import('@/pages/marketing/OurFeesPage'));
const ReviewsPage = routeLazy(() => import('@/pages/marketing/ReviewsPage'));
const ContactUsPage = routeLazy(() => import('@/pages/marketing/ContactUsPage'));
const AppointmentPage = routeLazy(() => import('@/pages/marketing/AppointmentPage'));
const PublicationsPage = routeLazy(() => import('@/pages/marketing/PublicationsPage'));
const ResourcesPage = routeLazy(() => import('@/pages/marketing/ResourcesPage'));
const PracticeAreasHub = routeLazy(() => import('@/pages/marketing/PracticeAreasHub'));
// Utility
const PrivacyPolicyPage = routeLazy(() => import('@/pages/utility/PrivacyPolicyPage'));
const DisclaimerPage = routeLazy(() => import('@/pages/utility/DisclaimerPage'));
const GlossaryPage = routeLazy(() => import('@/pages/utility/GlossaryPage'));
// Phase 4 — /nyc-* long-form articles (41 routes)
import { nycRoutes } from '@/pages/nyc/_routes';
import { legacyArticleSlugSet } from '@/data/legacyArticleSlugs';
import LegacyArticleTemplate from '@/components/LegacyArticleTemplate';
import ArticleLibraryPage from '@/pages/ArticleLibraryPage';
import ArticleHubPage from '@/pages/ArticleHubPage';
import { articleHubIdSet } from '@/data/articleHubs';
// Locations — /location/ index + per-location landing pages
const LocationIndexPage = routeLazy(() => import('@/pages/LocationIndexPage'));
import LocationTemplate from '@/components/LocationTemplate';
import { locations } from '@/data/locations';
// Practice sub-pages (family law cluster) + podcast
import PracticeSubPageTemplate from '@/components/PracticeSubPageTemplate';
import { practiceSubPages } from '@/data/practiceSubPages';
const ObsessedWithSuccessPage = routeLazy(() => import('@/pages/marketing/ObsessedWithSuccessPage'));

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      {/* Practice areas */}
      <Route path="/probate/" component={ProbatePage} />
      <Route path="/estate-planning/" component={EstatePlanningPage} />
      <Route path="/trusts/" component={TrustsPage} />
      <Route path="/asset-protection/" component={AssetProtectionPage} />
      <Route path="/guardianship/" component={GuardianshipPage} />
      <Route path="/power-of-attorney/" component={PowerOfAttorneyPage} />
      <Route path="/elder-law/" component={ElderLawPage} />
      <Route path="/family-law/" component={FamilyLawPage} />
      <Route path="/matrimonial/" component={MatrimonialPage} />
      <Route path="/real-estate/" component={RealEstatePage} />
      <Route path="/personal-injury/" component={PersonalInjuryPage} />
      <Route path="/medicaid-planning/" component={MedicaidPlanningPage} />
      <Route path="/wills/" component={WillsPage} />
      <Route path="/wills-and-trusts/" component={WillsAndTrustsPage} />
      <Route path="/civil-rights/" component={CivilRightsPage} />
      {/* Attorney bios */}
      <Route path="/attorneys/" component={AttorneysIndexPage} />
      <Route path="/russel-morgan-esq/" component={RusselMorganEsqPage} />
      <Route path="/russel-morgan-estate-planning-lawyer-in-nyc/" component={RusselMorganEPLawyerPage} />
      <Route path="/simge-karaboga-ozdemir/" component={SimgeKarabogaPage} />
      <Route path="/attorney-executive-advisor-at-morgan-legal-group/" component={GuyChiricoPage} />
      <Route path="/alan-vaitzman-esq/" component={AlanVaitzmanPage} />
      <Route path="/peter-gathings-esq/" component={PeterGathingsPage} />
      <Route path="/sofia-cristando-esq/" component={SofiaCristandoPage} />
      <Route path="/bianca-olliver-esq/" component={BiancaOlliverPage} />
      <Route path="/james-threatt-esq/" component={JamesThreattPage} />
      <Route path="/jacqueline-bohatch-esq/" component={JacquelineBohatchPage} />
      <Route path="/sabrina-vancamp-esq/" component={SabrinaVanCampPage} />
      <Route path="/allan-louis-esq/" component={AllanLouisPage} />
      <Route path="/shaniece-hunter-esq/" component={ShanieceHunterPage} />
      <Route path="/liza-gross-esq/" component={LizaGrossPage} />
      <Route path="/gabriella-vaitzman-esq/" component={GabriellaVaitzmanPage} />
      <Route path="/charles-marchese-esq/" component={CharlesMarchesePage} />
      {/* Marketing */}
      <Route path="/about-us/" component={AboutUsPage} />
      <Route path="/our-fees/" component={OurFeesPage} />
      <Route path="/reviews/" component={ReviewsPage} />
      <Route path="/contact-us/" component={ContactUsPage} />
      <Route path="/appointment/" component={AppointmentPage} />
      <Route path="/publications/" component={PublicationsPage} />
      <Route path="/resources/" component={ResourcesPage} />
      <Route path="/practice-areas/" component={PracticeAreasHub} />
      {/* Utility */}
      <Route path="/privacy-policy-page/" component={PrivacyPolicyPage} />
      <Route path="/disclaimer/" component={DisclaimerPage} />
      <Route path="/glossary-of-legal-terms/" component={GlossaryPage} />
      {/* /nyc-* long-form articles */}
      {nycRoutes.map((r) => (
        <Route key={r.url} path={r.url} component={r.component} />
      ))}
      {/* Locations */}
      <Route path="/location/" component={LocationIndexPage} />
      {locations.map((loc) => (
        <Route key={loc.url} path={loc.url}>{() => <LocationTemplate location={loc} />}</Route>
      ))}
      {/* Practice sub-pages (family law cluster) */}
      {practiceSubPages.map((p) => (
        <Route key={p.url} path={p.url}>{() => <PracticeSubPageTemplate page={p} />}</Route>
      ))}
      {/* Podcast + /practices/ alias to the practice-areas hub */}
      <Route path="/obsessed-with-success/" component={ObsessedWithSuccessPage} />
      <Route path="/practices/" component={PracticeAreasHub} />
      <Route path="/articles/" component={ArticleLibraryPage} />
      {/* Тематический узел. Объявлен ДО общего двухсегментного образца ниже,
          иначе перенесённая страница с путём /articles/<что-то>/ перехватила бы
          его — таких нет, но порядок объявления не должен зависеть от удачи. */}
      <Route path="/articles/:hub/">
        {(params) =>
          articleHubIdSet.has(params.hub) ? <ArticleHubPage id={params.hub} /> : <NotFound />
        }
      </Route>
      {/* Страницы, перенесённые из WordPress (корзина «ДЕРЖИТ», 734 адреса).
          Один маршрут с образцом вместо 734 объявлений: заголовки и описания
          приходят вместе с телом, клиенту достаточно списка адресов.
          Стоит последним перед 404 — все свои маршруты объявлены выше и
          перехватывают раньше, так что затмить живую страницу он не может. */}
      <Route path="/:slug/">
        {(params) =>
          legacyArticleSlugSet.has(params.slug)
            ? <LegacyArticleTemplate path={params.slug} />
            : <NotFound />
        }
      </Route>
      {/* Вложенные адреса вида /sexual-assault/…: у них путь из двух сегментов,
          и односегментный образец выше их не ловит. */}
      <Route path="/:parent/:slug/">
        {(params) => {
          const full = `${params.parent}/${params.slug}`;
          return legacyArticleSlugSet.has(full)
            ? <LegacyArticleTemplate path={full} />
            : <NotFound />;
        }}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          {/* Fallback is null: every route is prerendered, so the only moment it
              can show is the instant a lazily-split page is fetched. */}
          <Suspense fallback={null}>
            <Router />
          </Suspense>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
